import { Trans, useLingui } from "@lingui/react/macro";
import {
  informationFieldConfigValueSchema,
  patientKeys,
  updateDiagnosisCode,
  usePatientInformationFields,
} from "@/api/Patients";
import ErrorMessage from "@medoma/components/ErrorMessage/ErrorMessage";
import { FilledButton } from "@medoma/components/Button/Button";
import Form from "@/components/Form/Form";
import InputField from "@medoma/components/InputField/InputField";
import { Loading } from "@medoma/components/Loading/Loading";
import { deducedError } from "@/Utils/ErrorUtils";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useForm } from "react-hook-form";

export const EditDiagnosisCode = ({
  currentDiagnosisCode,
  onSuccess,
  patientId,
}: {
  currentDiagnosisCode: string | null | undefined;
  onSuccess: () => void;
  patientId: string;
}) => {
  const { t } = useLingui();
  const {
    formState: { errors, isDirty },
    getValues,
    handleSubmit,
    register,
    reset,
    setError,
  } = useForm({
    defaultValues: {
      diagnosisCode: currentDiagnosisCode ?? "",
    },
  });

  const queryClient = useQueryClient();
  const { mutate, isPending, isSuccess } = useMutation({
    mutationFn: (diagnosisCode: string) => {
      return updateDiagnosisCode(patientId, diagnosisCode);
    },
    onError: (error) => {
      setError("root.server", {
        message: deducedError(error),
      });
      // Reset `isDirty` to support only showing server error when the form is not changed.
      reset(getValues(), {
        keepErrors: true,
        keepIsSubmitted: true,
        keepTouched: true,
        keepIsValid: true,
        keepSubmitCount: true,
      });
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: patientKeys.detail(patientId),
      });
      onSuccess();
    },
  });

  const {
    data: conditionalInformationFields,
    isPending: isPendingConditionalInformationFields,
    isError: isErrorConditionalInformationFields,
    error: errorConditionalInformationFields,
  } = usePatientInformationFields();

  if (isPendingConditionalInformationFields) {
    return <Loading message={t`Hämtar information om valbara fält`} />;
  }

  if (isErrorConditionalInformationFields) {
    return (
      <ErrorMessage
        message={`${t`Kunde inte hämta information om valbara fält.`} ${deducedError(errorConditionalInformationFields)}`}
      />
    );
  }

  return (
    <Form
      onSubmit={handleSubmit((formData) => {
        mutate(formData.diagnosisCode);
      })}
    >
      {isPending || isSuccess ? (
        <Loading message={t`Ändrar diagnoskoden`} />
      ) : (
        <>
          {errors.root?.server?.message && !isDirty ? (
            <ErrorMessage message={errors.root.server.message} />
          ) : null}
          <Form.Row>
            <InputField
              label={t`Diagnoskod`}
              showOptionalLabel={false}
              errorMessage={errors.diagnosisCode?.message}
              {...register("diagnosisCode", {
                required: {
                  value:
                    conditionalInformationFields.Pet ===
                    informationFieldConfigValueSchema.enum.required,
                  message: t`Ange diagnoskod`,
                },
              })}
              width="fit"
            />
          </Form.Row>
          <FilledButton type="submit">
            <Trans>Spara ändringar</Trans>
          </FilledButton>
        </>
      )}
    </Form>
  );
};
